import React, { useState, useEffect, useCallback } from "react";
import TextField from "../../../Components/common/TextField";
import SearchField from "../../../Components/common/SearchField";
import MenuItem from "../../../Components/common/MenuItem";
import Grid from "@mui/material/Grid";
import Button from "../../../Components/common/Button";
import Checkbox from "../../../Components/common/Checkbox";
import { timeZonesTypes } from "../../../Components/Attributes/timezone";
import { checkPrivileges, errorHandler } from "../../../Helpers";
import Dialog from "../../../Components/common/Dialog";
import ConfirmDialog from "../../../Components/common/ConfirmDialoag";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Tooltip from "../../../Components/common/Tooltip";
import { connect } from "react-redux";
import instance from "../../../axios";
import { notifySuccess } from "../../../Utils/CustomNotifcations";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";
import withTranslationWrapper from "../../../HOC/withTranslation";

const frequencyType = [{ type: "s" }, { type: "m" }, { type: "h" }];

// ── Tab bar ─────────────────────────────────────────────────────────────────
function TabBar({ active, onTabChange, translate, themecolors }) {
  const tabs = [
    { key: "assigned", label: translate("commandsAssignedTabUser"), icon: <LinkIcon style={{ fontSize: 14, marginRight: 4 }} /> },
    { key: "schedule", label: translate("commandsScheduleTab"), icon: <CalendarTodayIcon style={{ fontSize: 14, marginRight: 4 }} /> },
  ];
  return (
    <div style={{ display: "flex", borderBottom: `2px solid ${themecolors.themeLightColor}44`, marginBottom: 12 }}>
      {tabs.map((tab) => (
        <button key={tab.key} onClick={() => onTabChange(tab.key)}
          style={{
            display: "flex", alignItems: "center", padding: "6px 14px",
            background: "transparent", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500,
            color: active === tab.key ? themecolors.menuActiveBackground : themecolors.textColor,
            borderBottom: active === tab.key ? `2px solid ${themecolors.menuActiveBackground}` : "2px solid transparent",
            marginBottom: -2, transition: "all 0.15s",
          }}>
          {tab.icon}{tab.label}
        </button>
      ))}
    </div>
  );
}

// ── Section header with count badge ─────────────────────────────────────────
function SectionHeader({ title, count, themecolors }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <strong style={{ fontSize: 12, fontWeight: 600, color: themecolors && themecolors.textColor }}>
        {title}
      </strong>
      <span style={{
        fontSize: 11, padding: "1px 7px", borderRadius: 10,
        background: themecolors ? `${themecolors.themeLightColor}44` : "#eee",
        color: themecolors?.textColor,
      }}>
        {count}
      </span>
    </div>
  );
}

// ── Single command row card ──────────────────────────────────────────────────
function CommandRow({
  item, mode, showBadge, canAssign, canUnassign, canEdit, canDelete, canSchedule,
  onAssign, onUnassign, onEdit, onDelete, onSchedule, translate, themecolors,
}) {
  const typeLabel = (translate && item.type) ? (translate(`commands.${item.type}`) || item.type) : item.type;
  const isCustomType = item.type === "custom";
  const badgeText = translate("customCommand");
  const accentColor = themecolors?.menuActiveBackground || "#1976d2";
  const displayName = item.description || typeLabel;

  let subtitle = "";
  if (isCustomType && item.attributes && item.attributes.data) {
    subtitle = item.attributes.data;
  } else if (!isCustomType) {
    const descKey = `commands.desc.${item.type}`;
    const descTranslated = translate ? translate(descKey) : descKey;
    subtitle = descTranslated !== descKey ? descTranslated : "";
  }

  return (
    <div style={{
      background: themecolors ? `${themecolors.themeLightColor}22` : "#f5f5f5",
      border: themecolors ? `1px solid ${themecolors.themeLightColor}44` : "1px solid #ddd",
      borderRadius: 4, marginBottom: 6, padding: "7px 10px",
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: themecolors?.textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displayName}
          </div>
          {showBadge && (
            <span style={{
              fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
              padding: "2px 8px", borderRadius: 10,
              border: `1px solid ${accentColor}`,
              background: `${accentColor}11`, color: accentColor,
            }}>
              {(badgeText || "").toUpperCase()}
            </span>
          )}
        </div>
        {subtitle ? (
          <div style={{ fontSize: 11, color: themecolors?.textColor, opacity: 0.65, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {subtitle}
          </div>
        ) : null}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
        {mode === "assigned" && canUnassign && onUnassign && (
          <Tooltip title={translate ? translate("unassign") : "Unassign"}>
            <Button size="small" onClick={() => onUnassign(item)}
              style={{ fontSize: 11, padding: "2px 8px", height: 26, background: "transparent", border: `1px solid ${accentColor}`, color: accentColor }}>
              <LinkOffIcon style={{ fontSize: 13, marginRight: 2 }} />
              {translate ? translate("unassign") : "Unassign"}
            </Button>
          </Tooltip>
        )}

        {mode === "pool" && (
          <>
            {canAssign && onAssign && (
              <Button size="small" onClick={() => onAssign(item)}
                style={{ fontSize: 11, padding: "2px 8px", height: 26, background: "transparent", border: `1px solid ${accentColor}`, color: accentColor }}>
                <LinkIcon style={{ fontSize: 13, marginRight: 2 }} />
                {translate ? translate("assign") : "Assign"}
              </Button>
            )}
            {canSchedule && onSchedule && (
              <Tooltip title={translate ? translate("schedule") : "Schedule"}>
                <CalendarTodayIcon className="actionButton" fontSize="small"
                  style={{ margin: "0 3px", cursor: "pointer", color: themecolors?.textColor }}
                  onClick={() => onSchedule(item)} />
              </Tooltip>
            )}
            {canEdit && onEdit && (
              <Tooltip title={translate ? translate("edit") : "Edit"}>
                <EditIcon className="actionButton" fontSize="small"
                  style={{ margin: "0 3px", cursor: "pointer", color: themecolors?.textColor }}
                  onClick={() => onEdit([item])} />
              </Tooltip>
            )}
            {canDelete && onDelete && (
              <Tooltip title={translate ? translate("delete") : "Delete"}>
                <DeleteIcon className="actionButton" fontSize="small"
                  style={{ margin: "0 3px", cursor: "pointer", color: themecolors?.textColor }}
                  onClick={() => onDelete([item])} />
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Scheduled command row ────────────────────────────────────────────────────
function ScheduledRow({ cmd, showDevice, devices, canUpdate, onEdit, onCancel, translate, themecolors }) {
  const accentColor = themecolors?.menuActiveBackground || "#1976d2";
  const formattedTime = cmd.scheduledTime
    ? moment(cmd.scheduledTime).format("MMM D, YYYY HH:mm")
    : "—";
  const name = cmd.description || (translate && translate(`commands.${cmd.type}`)) || cmd.type;
  const typeLabel = (translate && translate(`commands.${cmd.type}`)) || cmd.type;
  const device = showDevice && devices ? devices.find((d) => d.id === cmd.deviceId) : null;
  const metaParts = [typeLabel, device ? device.name : null].filter(Boolean);

  return (
    <div style={{
      background: themecolors ? `${themecolors.themeLightColor}22` : "#f5f5f5",
      border: themecolors ? `1px solid ${themecolors.themeLightColor}44` : "1px solid #ddd",
      borderRadius: 4, marginBottom: 6, padding: "7px 10px",
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: themecolors?.textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name}
        </div>
        {metaParts.length > 0 && (
          <div style={{ fontSize: 11, color: themecolors?.textColor, opacity: 0.65, marginTop: 1 }}>
            {metaParts.join(" · ")}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <span style={{ fontSize: 11, color: themecolors?.textColor, opacity: 0.7, display: "flex", alignItems: "center", gap: 3 }}>
          <CalendarTodayIcon style={{ fontSize: 13 }} />
          {formattedTime}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
          padding: "2px 8px", borderRadius: 10,
          background: accentColor, color: "#fff",
        }}>
          {translate ? translate("scheduled") : "Scheduled"}
        </span>
        {canUpdate && onEdit && (
          <Tooltip title={translate ? translate("edit") : "Edit"}>
            <EditIcon className="actionButton" fontSize="small"
              style={{ cursor: "pointer", color: themecolors?.textColor }}
              onClick={() => onEdit(cmd)} />
          </Tooltip>
        )}
        {canUpdate && onCancel && (
          <Tooltip title={translate ? translate("cancelSchedule") : "Cancel"}>
            <DeleteIcon className="actionButton" fontSize="small"
              style={{ cursor: "pointer", color: accentColor }}
              onClick={() => onCancel(cmd)} />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// ── Main SavedCommands ────────────────────────────────────────────────────────
function SavedCommands(props) {
  const {
    translate, themecolors, dispatch,
    commandsData, allCommands, devices,
    userId,
    savedCommandsHandle, savedCommands_Selection,
    visableSavedCommands, savedCommandsForm, savedCommands_handleChange,
    onCloseC_Att_Modal, onCreateNewSavedCommands, sendCommand: sendCommandProp,
    canRemove, canAssign: canAssignProp,
    onEdit, onDelete, onListSavedCommands,
    isHaveAccess, isAddVisableBtn, addButtonStatus, addSavedCommands,
  } = props;

  const t = useCallback((key) => translate(key), [translate]);

  const [activeTab, setActiveTab] = useState("assigned");
  const [search, setSearch] = useState("");
  const [scheduledCommands, setScheduledCommands] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleEditingCommand, setScheduleEditingCommand] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    scheduledTime: "",
    description: "",
    data: "", frequency: "", frequencyType: "s",
    timezone: "", phoneNumber: "", messages: "",
    index: "", server: "", port: "", radius: "", enable: false,
  });
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [cancelScheduleOpen, setCancelScheduleOpen] = useState(false);
  const [cancellingSchedule, setCancellingSchedule] = useState(null);

  useEffect(() => {
    fetchScheduledCommands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScheduledCommands = () => {
    if (!userId) return;
    setScheduledLoading(true);
    instance({ url: "/api/scheduledCommands", method: "GET", params: { userId } })
      .then((res) => {
        let list = [];
        if (Array.isArray(res)) list = res;
        else if (res && Array.isArray(res.data)) list = res.data;
        else if (res && res.data && Array.isArray(res.data.data)) list = res.data.data;
        setScheduledCommands(list.filter((c) => (c.status || "PENDING") === "PENDING"));
        setScheduledLoading(false);
      })
      .catch(() => setScheduledLoading(false));
  };

  const getAssignedItems = () => (commandsData || []).filter((item) => item.check === true);
  const getPoolItems = () => (commandsData || []).filter((item) => item.check !== true);

  const filterItems = (items) => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const label = item.description || item.type || "";
      return label.toLowerCase().includes(q);
    });
  };

  const handleAssign = (item) => {
    if (savedCommands_Selection) {
      savedCommands_Selection(true, item);
    }
  };

  const handleUnassign = (item) => {
    if (savedCommands_Selection) {
      savedCommands_Selection(false, item);
    }
  };

  // ── Schedule ──
  const openScheduleEdit = (command) => {
    const attrs = command.attributes || {};
    setScheduleOpen(true);
    setScheduleEditingCommand(command);
    setScheduleForm({
      scheduledTime: command.scheduledTime || moment().add(1, "hour").startOf("hour").toISOString(),
      description: command.description || "",
      data: attrs.data || "", frequency: attrs.frequency || "", frequencyType: attrs.frequencyType || "s",
      timezone: attrs.timezone || "", phoneNumber: attrs.phoneNumber || "", messages: attrs.messages || "",
      index: attrs.index || "", server: attrs.server || "", port: attrs.port || "",
      radius: attrs.radius || "", enable: Boolean(attrs.enable),
    });
  };

  const closeSchedule = () => {
    setScheduleOpen(false);
    setScheduleEditingCommand(null);
  };

  const handleScheduleFormChange = (field, value) => {
    setScheduleForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitSchedule = async () => {
    if (!scheduleEditingCommand) return;

    const attrs = {};
    if (scheduleForm.data) attrs.data = scheduleForm.data;
    if (scheduleForm.frequency) attrs.frequency = scheduleForm.frequency;
    if (scheduleForm.frequencyType) attrs.frequencyType = scheduleForm.frequencyType;
    if (scheduleForm.timezone) attrs.timezone = scheduleForm.timezone;
    if (scheduleForm.phoneNumber) attrs.phoneNumber = scheduleForm.phoneNumber;
    if (scheduleForm.messages) attrs.messages = scheduleForm.messages;
    if (scheduleForm.index) attrs.index = scheduleForm.index;
    if (scheduleForm.server) attrs.server = scheduleForm.server;
    if (scheduleForm.port) attrs.port = scheduleForm.port;
    if (scheduleForm.radius) attrs.radius = scheduleForm.radius;
    if (scheduleForm.enable) attrs.enable = scheduleForm.enable;

    const payload = {
      id: scheduleEditingCommand.id,
      commandId: scheduleEditingCommand.commandId,
      deviceId: scheduleEditingCommand.deviceId,
      type: scheduleEditingCommand.type,
      description: scheduleForm.description,
      textChannel: false,
      scheduledTime: scheduleForm.scheduledTime,
      attributes: attrs,
    };

    setScheduleSaving(true);
    try {
      await instance({
        url: `/api/scheduledCommands/${scheduleEditingCommand.id}`, method: "PUT",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        data: payload,
      });
      dispatch(notifySuccess(t("scheduleUpdated")));
      closeSchedule();
      fetchScheduledCommands();
    } catch (error) {
      errorHandler(error, dispatch);
    } finally {
      setScheduleSaving(false);
    }
  };

  const openCancelSchedule = (command) => {
    setCancelScheduleOpen(true);
    setCancellingSchedule(command);
  };

  const submitCancelSchedule = async () => {
    try {
      await instance({
        url: `/api/scheduledCommands/${cancellingSchedule.id}/cancel`, method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      dispatch(notifySuccess(t("scheduleCancelled")));
      setCancelScheduleOpen(false);
      setCancellingSchedule(null);
      fetchScheduledCommands();
    } catch (error) {
      errorHandler(error, dispatch);
    }
  };

  // ── Render schedule form fields ──
  const renderScheduleFormFields = () => {
    const type = scheduleEditingCommand ? scheduleEditingCommand.type : "custom";

    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label={translate("scheduledTime")}
              value={scheduleForm.scheduledTime ? moment(scheduleForm.scheduledTime) : null}
              onChange={(v) => handleScheduleFormChange("scheduledTime", v ? v.toISOString() : "")}
              slotProps={{ textField: { size: "small", variant: "outlined", fullWidth: true, margin: "dense" } }}
              minDateTime={moment()}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField margin="dense" label={translate("sharedDescription")} variant="outlined" fullWidth
            value={scheduleForm.description}
            onChange={(e) => handleScheduleFormChange("description", e.target.value)} />
        </Grid>
        {type === "custom" && (
          <Grid item xs={12}>
            <TextField margin="dense" label={translate("commandData")} variant="outlined" fullWidth
              value={scheduleForm.data}
              onChange={(e) => handleScheduleFormChange("data", e.target.value)} />
          </Grid>
        )}
        {type === "positionPeriodic" && (
          <>
            <Grid item xs={6}>
              <TextField type="number" margin="dense" label={translate("commandFrequency")} variant="outlined" fullWidth
                value={scheduleForm.frequency}
                onChange={(e) => handleScheduleFormChange("frequency", e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField select margin="dense" label={translate("commandFrequencyType")} variant="outlined" fullWidth
                value={scheduleForm.frequencyType}
                onChange={(e) => handleScheduleFormChange("frequencyType", e.target.value)}>
                {frequencyType.map((ft) => <MenuItem key={ft.type} value={ft.type}>{ft.type}</MenuItem>)}
              </TextField>
            </Grid>
          </>
        )}
        {type === "setTimezone" && (
          <Grid item xs={12}>
            <TextField select margin="dense" label={translate("commandTimezone")} variant="outlined" fullWidth
              value={scheduleForm.timezone}
              onChange={(e) => handleScheduleFormChange("timezone", e.target.value)}>
              {timeZonesTypes.map((tz) => <MenuItem key={tz.key} value={tz.key}>{tz.label}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        {["sendSms", "sendUssd", "sosNumber"].includes(type) && (
          <Grid item xs={12}>
            <TextField margin="dense" label={translate("commandPhone")} variant="outlined" fullWidth
              value={scheduleForm.phoneNumber}
              onChange={(e) => handleScheduleFormChange("phoneNumber", e.target.value)} />
          </Grid>
        )}
        {["sendSms", "message"].includes(type) && (
          <Grid item xs={12}>
            <TextField margin="dense" label={translate("Messages")} variant="outlined" fullWidth
              value={scheduleForm.messages}
              onChange={(e) => handleScheduleFormChange("messages", e.target.value)} />
          </Grid>
        )}
      </Grid>
    );
  };

  // ── Render schedule tab ──
  const renderScheduleTab = () => {
    const canUpdate = checkPrivileges("commandUpdate");
    const q = (search || "").trim().toLowerCase();

    const filtered = scheduledCommands.filter((cmd) => {
      if (!q) return true;
      return (
        (cmd.description || "").toLowerCase().includes(q) ||
        (cmd.type || "").toLowerCase().includes(q)
      );
    }).sort((a, b) => new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime());

    const emptyText = q ? translate("noCommandsMatchSearch") : translate("noScheduledCommandsUser");

    return (
      <>
        <SectionHeader
          title={translate("scheduledForThisUser")}
          count={scheduledLoading ? "…" : filtered.length}
          themecolors={themecolors}
        />
        {scheduledLoading ? (
          <div style={{ fontSize: 12, opacity: 0.6, padding: "4px 0", color: themecolors?.textColor }}>
            {translate("loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ fontSize: 12, opacity: 0.6, padding: "4px 0", color: themecolors?.textColor }}>
            {emptyText}
          </div>
        ) : (
          filtered.map((cmd) => (
            <ScheduledRow
              key={cmd.id}
              cmd={cmd}
              showDevice
              devices={devices}
              canUpdate={canUpdate}
              onEdit={openScheduleEdit}
              onCancel={openCancelSchedule}
              translate={translate}
              themecolors={themecolors}
            />
          ))
        )}
      </>
    );
  };

  // ── Main render ──
  const assignedItems = filterItems(getAssignedItems());
  const poolItems = filterItems(getPoolItems());
  const canCreate = checkPrivileges("commandCreate");
  const canUpdate = checkPrivileges("commandUpdate");
  const canDelete = checkPrivileges("commandDelete");
  const scheduleTitle = translate("editScheduledCommand");

  return (
    <>
      <Grid container style={{ height: "100%", padding: "8px 0px" }}>
        {/* Header */}
        <Grid item xs={12}>
          <div style={{ textAlign: "right", padding: "2px 0 6px 0", display: "flex", alignItems: "center" }}
            className="clearfix">
            <h4 className="v-tab-heading pull-left with-border" style={{ marginTop: 8, marginBottom: 4 }}>
              {translate("sharedSavedCommand")}
            </h4>
            <div style={{ margin: "auto" }} />
            <div style={{ width: 200 }}>
              <SearchField
                fullWidth
                label={translate("sharedSearch")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canCreate && sendCommandProp && (
              <div style={{ marginTop: 8, marginBottom: 4, marginLeft: 8 }}>
                <Button size="small" onClick={() => onCreateNewSavedCommands && onCreateNewSavedCommands()}>
                  + {translate("sharedCreate")}
                </Button>
              </div>
            )}
          </div>
        </Grid>

        {/* Tab bar */}
        <Grid item xs={12}>
          <TabBar
            active={activeTab}
            onTabChange={(tab) => { setActiveTab(tab); setSearch(""); }}
            translate={translate}
            themecolors={themecolors}
          />
        </Grid>

        {/* Assigned tab */}
        {activeTab === "assigned" && (
          <>
            {/* Available Commands */}
            <Grid item xs={12} style={{ marginBottom: 12 }}>
              <SectionHeader title={translate("availableCommands")} count={assignedItems.length} themecolors={themecolors} />
              {assignedItems.length === 0 ? (
                <div style={{ fontSize: 12, opacity: 0.6, padding: "4px 0", color: themecolors?.textColor }}>
                  {translate("noCommandsAssignedUser")}
                </div>
              ) : (
                assignedItems.map((item) => (
                  <CommandRow
                    key={item.id}
                    item={item}
                    mode="assigned"
                    showBadge
                    canUnassign={canRemove}
                    onUnassign={handleUnassign}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    translate={translate}
                    themecolors={themecolors}
                  />
                ))
              )}
            </Grid>

            {/* Custom Commands Pool */}
            <Grid item xs={12}>
              <SectionHeader title={translate("customCommandsPool")} count={poolItems.length} themecolors={themecolors} />
              {poolItems.length === 0 ? (
                <div style={{ fontSize: 12, opacity: 0.6, padding: "4px 0", color: themecolors?.textColor }}>
                  {allCommands && allCommands.length === 0 ? translate("noCustomCommands") : translate("allCustomCommandsAssignedUser")}
                </div>
              ) : (
                poolItems.map((item) => (
                  <CommandRow
                    key={item.id}
                    item={item}
                    mode="pool"
                    canAssign={canAssignProp}
                    canEdit={canUpdate}
                    canDelete={canDelete}
                    onAssign={handleAssign}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    translate={translate}
                    themecolors={themecolors}
                  />
                ))
              )}
            </Grid>
          </>
        )}

        {/* Schedule tab */}
        {activeTab === "schedule" && (
          <Grid item xs={12}>
            {renderScheduleTab()}
          </Grid>
        )}

        {/* Create / Edit Command Dialog (wired to parent) */}
        {visableSavedCommands && (
          <Dialog
            open
            title={translate("Command")}
            themeColors={themecolors}
            disableFooter
            onClose={onCloseC_Att_Modal}
            dialogHeight={185}
            draggable
            bodyPadding={0}
            maxWidth="xs"
          >
            <div style={{ padding: "1px 20px", borderLeft: "1px solid " + (themecolors && themecolors.themeLightColor), height: "100%" }}>
              <h4>{translate("sharedSavedCommand")}</h4>
              <TextField
                id="description"
                margin="dense"
                label={translate("sharedDescription")}
                variant="outlined"
                fullWidth
                value={savedCommandsForm.description}
                onChange={savedCommands_handleChange("description")}
              />
              <TextField
                id="type" name="type" select
                margin="dense"
                label={translate("sharedType")}
                variant="outlined"
                fullWidth
                value={savedCommandsForm.type || "custom"}
                onChange={savedCommands_handleChange("type")}
              >
                {["custom","positionPeriodic","engineStop","engineResume","alarmArm","alarmDisarm","setTimezone","sendSms","rebootDevice"].map((tp) => (
                  <MenuItem key={tp} value={tp}>{translate(`commands.${tp}`) || tp}</MenuItem>
                ))}
              </TextField>
              {savedCommandsForm.type === "custom" && (
                <TextField
                  id="data" name="data"
                  margin="dense"
                  label={translate("commandData")}
                  variant="outlined"
                  fullWidth
                  value={savedCommandsForm.attributes.data}
                  onChange={savedCommands_handleChange("attributes")}
                />
              )}
              {savedCommandsForm.type === "positionPeriodic" && (
                <div>
                  <TextField id="frequency" name="frequency" type="number" margin="dense"
                    label={translate("commandFrequency")} variant="outlined" fullWidth
                    value={savedCommandsForm.attributes.frequency}
                    onChange={savedCommands_handleChange("attributes")} />
                  <TextField id="frequencyType" name="frequencyType" select
                    value={savedCommandsForm.attributes.frequencyType}
                    onChange={savedCommands_handleChange("attributes")}
                    margin="dense" fullWidth>
                    {frequencyType.map((option) => (
                      <MenuItem key={option.type} value={option.type}>{option.type}</MenuItem>
                    ))}
                  </TextField>
                </div>
              )}
              {savedCommandsForm.type === "setTimezone" && (
                <TextField id="timezone" name="timezone" select
                  label={translate("commandTimezone")}
                  value={savedCommandsForm.timezone}
                  onChange={savedCommands_handleChange("attributes")}
                  margin="dense" fullWidth>
                  {timeZonesTypes.map((option) => (
                    <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                  ))}
                </TextField>
              )}
              {["sendSms", "sendUssd", "sosNumber"].includes(savedCommandsForm.type) && (
                <TextField id="phoneNumber" name="phoneNumber" margin="dense"
                  label={translate("commandPhone")} variant="outlined" fullWidth
                  value={savedCommandsForm.phoneNumber}
                  onChange={savedCommands_handleChange("attributes")} />
              )}
              {["sendSms", "message"].includes(savedCommandsForm.type) && (
                <TextField id="messages" name="messages" margin="dense"
                  label={translate("Messages")} variant="outlined" fullWidth
                  value={savedCommandsForm.messages}
                  onChange={savedCommands_handleChange("attributes")} />
              )}
              {["alarmFall","alarmRemove","alarmBattery","modeDeepSleep","modePowerSaving","setAgps","voiceMonitoring"].includes(savedCommandsForm.type) && (
                <Checkbox
                  checked={savedCommandsForm.enable}
                  onChange={savedCommands_handleChange("attributes")}
                  color="primary"
                  label={translate("commandEnable")}
                  name="enable"
                />
              )}
              {savedCommandsForm.type === "setConnection" && (
                <div>
                  <TextField id="server" name="server" margin="dense"
                    label={translate("commandServer")} variant="outlined" fullWidth
                    value={savedCommandsForm.server}
                    onChange={savedCommands_handleChange("attributes")} />
                  <TextField id="port" name="port" type="number" margin="dense"
                    label={translate("commandPort")} variant="outlined" fullWidth
                    value={savedCommandsForm.port}
                    onChange={savedCommands_handleChange("attributes")} />
                </div>
              )}
              {savedCommandsForm.type === "novementAlarm" && (
                <TextField id="radius" name="radius" margin="dense"
                  label={translate("commandRadius")} variant="outlined" fullWidth
                  value={savedCommandsForm.radius}
                  onChange={savedCommands_handleChange("attributes")} />
              )}
              <div style={{ textAlign: "right", marginTop: 10, marginBottom: 16 }}>
                <Button size="small" style={{ marginRight: 15 }} className="button-white"
                  onClick={() => onCloseC_Att_Modal()}>
                  {translate("sharedCancel")}
                </Button>
                {checkPrivileges(isHaveAccess) && (
                  <Button disabled={!isAddVisableBtn} size="small"
                    onClick={() => addSavedCommands()}>
                    {translate(addButtonStatus)}
                  </Button>
                )}
              </div>
            </div>
          </Dialog>
        )}
      </Grid>

      {/* Cancel schedule confirm */}
      {cancelScheduleOpen && (
        <ConfirmDialog
          title={translate("areYouSureToCancelSchedule")}
          onCancel={() => { setCancelScheduleOpen(false); setCancellingSchedule(null); }}
          children={cancellingSchedule && (cancellingSchedule.description || translate(`commands.${cancellingSchedule.type}`))}
          onOk={submitCancelSchedule}
        />
      )}

      {/* Schedule edit dialog */}
      {scheduleOpen && (
        <Dialog
          open
          title={scheduleTitle}
          themeColors={themecolors}
          disableFooter
          onClose={closeSchedule}
          dialogHeight={200}
          draggable
          bodyPadding={0}
          maxWidth="xs"
        >
          <div style={{ padding: "4px 20px 16px" }}>
            {renderScheduleFormFields()}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <Button size="small" className="button-white" onClick={closeSchedule} disabled={scheduleSaving}>
                {translate("sharedCancel")}
              </Button>
              <Button size="small" onClick={submitSchedule}
                disabled={scheduleSaving || !scheduleForm.scheduledTime}>
                {scheduleSaving ? translate("loading") : (scheduleEditingCommand ? translate("sharedUpdate") : translate("schedule"))}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  allCommands: state.commands,
  devices: state.devices3 || (state.devices && state.devices.data) || [],
});
export default connect(mapStateToProps)(withTranslationWrapper(SavedCommands));
